import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import Swal from 'sweetalert2';
import { SpinnerService } from 'src/app/shared/services/spinner.service';
import { ShinkansenPayoutService, ShinkansenPayoutRequest } from 'src/app/dashboard/services/shinkansen-payout.service';

@Component({
  selector: 'app-pay-shinkansen-modal',
  templateUrl: './pay-shinkansen-modal.component.html',
  styleUrls: ['./pay-shinkansen-modal.component.sass']
})
export class PayShinkansenModalComponent {
  datosForm: FormGroup | undefined;
  transaction: any | undefined;

  // Opciones para tipo de cuenta según la documentación de Shinkansen
  accountTypes = [
    { value: 'current_account', label: 'Cuenta Corriente' },
    { value: 'savings_account', label: 'Cuenta de Ahorro' },
    { value: 'sight_account', label: 'Cuenta Vista' }
  ];

  // fin_ids canónicos de Shinkansen — se aceptan tal cual (identity).
  shinkansenValidCodes: string[] = [
    'BANCO_DE_CHILE_CL', 'BANCO_ESTADO_CL', 'BANCO_SANTANDER_CL', 'BANCO_SECURITY_CL',
    'BANCO_FALABELLA_CL', 'BANCO_RIPLEY_CL', 'BANCO_CONSORCIO_CL', 'SCOTIABANK_CL',
    'BCI_CL', 'BICE_CL', 'ITAU_CL', 'HSBC_CL', 'MERCADO_PAGO_CL', 'TENPO_CL',
    'TENPO_BANK_CL', 'GLOBAL_66_CL', 'COOPEUCH_CL',
  ];

  // Aliases / nombres → fin_id Shinkansen, keyed por la forma NORMALIZADA
  // (`normalizeBankName`): sin acentos, sin paréntesis, sin prefijo "BANCO",
  // sin sufijo "_CL", separadores colapsados. Cubre los nombres reales de la
  // tabla `bancos` (CL) — ej. "Banco de Crédito e Inversiones (BCI)".
  // ESPEJO del backend `shinkansen-mappings.util.ts`.
  bankMapping: { [key: string]: string } = {
    // Banco de Chile / Edwards (fusión)
    'CHILE': 'BANCO_DE_CHILE_CL',
    'DE CHILE': 'BANCO_DE_CHILE_CL',
    'DE CHILE / EDWARDS': 'BANCO_DE_CHILE_CL',
    'CHILE / EDWARDS': 'BANCO_DE_CHILE_CL',
    'EDWARDS': 'BANCO_DE_CHILE_CL',
    // Banco Estado
    'ESTADO': 'BANCO_ESTADO_CL',
    'DEL ESTADO': 'BANCO_ESTADO_CL',
    // BCI
    'BCI': 'BCI_CL',
    'DE CREDITO E INVERSIONES': 'BCI_CL',
    'CREDITO E INVERSIONES': 'BCI_CL',
    // BICE
    'BICE': 'BICE_CL',
    // HSBC
    'HSBC': 'HSBC_CL',
    'HSBC BANK': 'HSBC_CL',
    // Santander
    'SANTANDER': 'BANCO_SANTANDER_CL',
    // Itaú / Corpbanca (fusión)
    'ITAU': 'ITAU_CL',
    'CORPBANCA': 'ITAU_CL',
    'ITAU / CORPBANCA': 'ITAU_CL',
    'ITAU CORPBANCA': 'ITAU_CL',
    // Security
    'SECURITY': 'BANCO_SECURITY_CL',
    // Scotiabank
    'SCOTIABANK': 'SCOTIABANK_CL',
    // Falabella
    'FALABELLA': 'BANCO_FALABELLA_CL',
    // Ripley
    'RIPLEY': 'BANCO_RIPLEY_CL',
    // Consorcio
    'CONSORCIO': 'BANCO_CONSORCIO_CL',
    // Billeteras / prepago
    'MERCADO PAGO': 'MERCADO_PAGO_CL',
    'TENPO': 'TENPO_CL',
    'TENPO PREPAGO': 'TENPO_CL',
    'PREPAGO TENPO': 'TENPO_CL',
    'TENPO BANK': 'TENPO_BANK_CL',
    'TENPO BANCO': 'TENPO_BANK_CL',
    'GLOBAL 66': 'GLOBAL_66_CL',
    'COOPEUCH': 'COOPEUCH_CL',
  };

  constructor(
    private fb: FormBuilder,
    private spinner: SpinnerService,
    private shinkansenPayoutService: ShinkansenPayoutService,
    public dialogRef: MatDialogRef<PayShinkansenModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    this.transaction = data;
    this.initForm();
  }

  /**
   * Calcula el dígito verificador de un RUT chileno
   * @param rut - RUT sin el dígito verificador
   * @returns El dígito verificador esperado (string)
   */
  calculateRutDV(rut: string): string {
    let sum = 0;
    let multiplier = 2;

    // Recorrer el RUT de derecha a izquierda
    for (let i = rut.length - 1; i >= 0; i--) {
      sum += parseInt(rut[i]) * multiplier;
      multiplier = multiplier === 7 ? 2 : multiplier + 1;
    }

    const remainder = sum % 11;
    const dv = 11 - remainder;

    if (dv === 11) return '0';
    if (dv === 10) return 'K';
    return dv.toString();
  }

  /**
   * Validador personalizado para el RUT
   * Verifica formato, longitud y dígito verificador
   */
  validateRut(control: FormControl): { [key: string]: any } | null {
    if (!control.value) return null;

    // Remover guiones, puntos y espacios para contar solo caracteres del RUT
    const cleanRut = control.value.replace(/[.\-\s]/g, '').toUpperCase();

    // Verificar que solo contenga números y letras (K al final)
    const rutPattern = /^[0-9]+[0-9K]?$/;
    if (!rutPattern.test(cleanRut)) {
      return { invalidFormat: true };
    }

    // Verificar que no exceda 10 caracteres
    if (cleanRut.length > 10) {
      return { maxLength: true };
    }

    // Verificar que tenga al menos 2 caracteres (número + DV)
    if (cleanRut.length < 2) {
      return { invalidFormat: true };
    }

    // Validar dígito verificador
    const rutNumber = cleanRut.slice(0, -1); // Todo menos el último carácter
    const dvProvided = cleanRut.slice(-1); // Último carácter
    const dvExpected = this.calculateRutDV(rutNumber);

    if (dvProvided !== dvExpected) {
      return { invalidDV: true };
    }

    return null;
  }

  /**
   * Formatea el RUT agregando guión antes del dígito verificador
   * Ejemplo: 123456789 -> 12345678-9
   */
  formatRut(rut: string): string {
    if (!rut) return '';

    // Remover cualquier formato existente (puntos, guiones, espacios)
    const cleanRut = rut.replace(/[.\-\s]/g, '');

    // Si tiene al menos 2 caracteres, agregar guión antes del último
    if (cleanRut.length >= 2) {
      return `${cleanRut.slice(0, -1)}-${cleanRut.slice(-1)}`;
    }

    return cleanRut;
  }

  /**
   * Normaliza un nombre/código de banco a una forma canónica comparable:
   * quita acentos, mayúsculas, elimina paréntesis ("(BCI)"), underscores→espacio,
   * colapsa espacios, quita prefijo "BANCO " y sufijo " CL".
   * Ej: "Banco de Crédito e Inversiones (BCI)" -> "DE CREDITO E INVERSIONES"
   * ESPEJO del backend `shinkansen-mappings.util.ts#normalizeBankName`.
   */
  normalizeBankName(input: string): string {
    if (!input) return '';
    let s = input.normalize('NFD').replace(/[̀-ͯ]/g, ''); // sin acentos
    s = s.toUpperCase();
    s = s.replace(/\([^)]*\)/g, ' ');  // quitar paréntesis
    s = s.replace(/_+/g, ' ');         // underscores → espacio
    s = s.replace(/\s+/g, ' ').trim();
    s = s.replace(/^BANCO\s+/, '');    // prefijo
    s = s.replace(/\s+CL$/, '');       // sufijo país
    return s.trim();
  }

  /**
   * Mapea el nombre del banco al código de Shinkansen (fin_id).
   * Robusto a acentos/paréntesis del nombre de la tabla `bancos`.
   * Ejemplo: "Banco de Crédito e Inversiones (BCI)" -> "BCI_CL"
   */
  mapBankToShinkansenCode(bankName: string): string {
    if (!bankName) return '';

    // 1. Ya es un fin_id canónico (BCI_CL, BANCO_DE_CHILE_CL, ...)
    const upperRaw = bankName.trim().toUpperCase();
    if (this.shinkansenValidCodes.indexOf(upperRaw) !== -1) {
      return upperRaw;
    }

    // 2. Alias / nombre real (acento/paréntesis-insensible)
    const norm = this.normalizeBankName(bankName);
    if (this.bankMapping[norm]) {
      return this.bankMapping[norm];
    }

    // 3. Fallback best-effort. El backend revalida y bloquea si no reconoce el
    //    banco, así que esto no envía pagos a ciegas (ver guard en payout-sender).
    return norm ? `${norm.replace(/\s+/g, '_')}_CL` : '';
  }

  initForm() {
    // Mapear tipo de cuenta del sistema al formato de Shinkansen
    let accountType = 'current_account'; // default
    let accountTypeLabel = 'Cuenta Corriente'; // default para mostrar

    if (this.transaction?.tipoCuentaNombre) {
      const tipoCuenta = this.transaction.tipoCuentaNombre.toLowerCase();
      if (tipoCuenta.includes('corriente')) {
        accountType = 'current_account';
        accountTypeLabel = 'Cuenta Corriente';
      } else if (tipoCuenta.includes('ahorro')) {
        accountType = 'savings_account';
        accountTypeLabel = 'Cuenta de Ahorro';
      } else if (tipoCuenta.includes('vista') || tipoCuenta.includes('rut')) {
        accountType = 'sight_account';
        accountTypeLabel = 'Cuenta Vista';
      }
    }

    // Formatear el RUT con guión
    const formattedRut = this.formatRut(this.transaction?.numeroId || '');

    // Mapear el banco al código de Shinkansen
    const shinkansenBankCode = this.mapBankToShinkansenCode(this.transaction?.bancoNombre || '');

    this.datosForm = this.fb.group({
      amount: new FormControl(this.transaction?.totalMonedaDestino || 0, [Validators.required, Validators.min(1)]),
      currency: new FormControl(this.transaction?.monedaDestino || 'CLP', [Validators.required]),
      creditorName: new FormControl(this.transaction?.alias || '', [Validators.required]),
      creditorRut: new FormControl(formattedRut, [Validators.required, this.validateRut.bind(this)]),
      creditorBank: new FormControl(shinkansenBankCode, [Validators.required]), // Código para enviar al backend (BCI_CL)
      creditorAccountNumber: new FormControl(this.transaction?.numeroCuenta || '', [Validators.required]),
      creditorAccountType: new FormControl(accountType, [Validators.required]), // Código para enviar (current_account, sight_account, etc)
      creditorAccountTypeLabel: new FormControl(accountTypeLabel), // Label para mostrar en español
      creditorEmail: new FormControl(this.transaction?.emailUsuario || ''),
      description: new FormControl(`Retiro #${this.transaction?.id || ''}`),
      reference: new FormControl(''),
    });
  }

  async processPayout() {
    if (this.datosForm?.invalid || !this.transaction?.id) {
      // Verificar errores específicos del RUT
      const rutControl = this.datosForm?.get('creditorRut');
      if (rutControl?.errors) {
        let errorMessage = 'Por favor complete todos los campos requeridos';

        if (rutControl.errors['maxLength']) {
          errorMessage = 'El RUT no puede tener más de 10 dígitos (sin contar el guión). Por favor verifique el número ingresado.';
        } else if (rutControl.errors['invalidDV']) {
          errorMessage = 'El dígito verificador del RUT es inválido. Por favor verifique que el RUT sea correcto.';
        } else if (rutControl.errors['invalidFormat']) {
          errorMessage = 'El formato del RUT no es válido. Debe contener solo números y opcionalmente la letra K al final.';
        } else if (rutControl.errors['required']) {
          errorMessage = 'El RUT es requerido para procesar el pago.';
        }

        Swal.fire({
          icon: 'warning',
          title: 'Error en RUT',
          text: errorMessage,
          confirmButtonColor: '#1e5a3f',
        });
        return;
      }

      Swal.fire({
        icon: 'warning',
        title: 'Datos incompletos',
        text: 'Por favor complete todos los campos requeridos',
        confirmButtonColor: '#1e5a3f',
      });
      return;
    }

    const spinnerRef = this.spinner.start();

    // Obtener el userId del cliente (puede venir como usuarioId o userId)
    const clientUserId = this.transaction?.usuarioId || this.transaction?.userId;

    if (!this.transaction?.id) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'No se pudo obtener el ID de la transacción. Por favor, recargue la página e intente nuevamente.',
        confirmButtonColor: '#dc2626',
      });
      return;
    }

    if (!clientUserId) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'No se pudo obtener el ID del cliente. Por favor, recargue la página e intente nuevamente.',
        confirmButtonColor: '#dc2626',
      });
      return;
    }

    const payload: ShinkansenPayoutRequest = {
      amount: this.datosForm!.get('amount')?.value,
      currency: this.datosForm!.get('currency')?.value,
      creditorName: this.datosForm!.get('creditorName')?.value,
      creditorRut: this.datosForm!.get('creditorRut')?.value,
      creditorBank: this.datosForm!.get('creditorBank')?.value,
      creditorAccountNumber: this.datosForm!.get('creditorAccountNumber')?.value,
      creditorAccountType: this.datosForm!.get('creditorAccountType')?.value,
      creditorEmail: this.datosForm!.get('creditorEmail')?.value || undefined,
      description: this.datosForm!.get('description')?.value || undefined,
      reference: this.datosForm!.get('reference')?.value || undefined,
      walletTransactionId: String(this.transaction.id), // ID de la transacción de retiro (se guarda en reference)
      usuarioId: clientUserId, // ID del usuario
    };


    this.shinkansenPayoutService.createPayout(payload).subscribe({
      next: (result) => {
        this.spinner.stop(spinnerRef);

        Swal.fire({
          icon: 'success',
          title: 'Pago procesado exitosamente',
          html: `
            <div style="text-align: left; padding: 0.5rem;">
              <p style="margin-bottom: 1rem;">${result.message}</p>
              <hr style="margin: 1rem 0; border: none; border-top: 1px solid #e5e7eb;">
              <p style="margin: 0.5rem 0;"><strong>ID de Transacción:</strong> ${result.payout.transactionId}</p>
              <p style="margin: 0.5rem 0;"><strong>Estado:</strong> <span style="color: #059669; font-weight: 600;">${result.payout.status}</span></p>
              <p style="margin: 0.5rem 0;"><strong>Monto:</strong> ${result.payout.currency} $${result.payout.amount.toLocaleString()}</p>
            </div>
          `,
          confirmButtonText: 'Aceptar',
          confirmButtonColor: '#1e5a3f',
        });
        this.dialogRef.close(true);
      },
      error: (error: any) => {
        this.spinner.stop(spinnerRef);

        // Determinar el mensaje principal del error
        const mainMessage = error?.message || error?.error?.message || 'Ha ocurrido un error al procesar el pago con Shinkansen';

        // Construir mensaje de error detallado
        let errorHtml = `<p>${mainMessage}</p>`;

        // Agregar detalles del error si existen
        const errorDetails = error?.details || error?.error?.details;

        // Extraer código de error de Shinkansen si existe
        let shinkansenErrorCode: string | null = null;
        if (Array.isArray(errorDetails) && errorDetails.length > 0 && errorDetails[0].error_code) {
          shinkansenErrorCode = errorDetails[0].error_code;
        }

        if (errorDetails) {
          errorHtml += '<hr style="margin: 1rem 0; border: none; border-top: 1px solid #fee2e2;">';
          errorHtml += '<div style="text-align: left; font-size: 0.875rem; color: #991b1b;">';
          errorHtml += '<p style="margin-bottom: 0.5rem;"><strong>Detalles del error:</strong></p>';

          if (typeof errorDetails === 'string') {
            errorHtml += `<p style="margin: 0.25rem 0;">• ${errorDetails}</p>`;
          } else if (Array.isArray(errorDetails)) {
            errorDetails.forEach((detail: any) => {
              // Errores de Shinkansen con error_code, error_message, transaction_id
              if (detail.error_code || detail.error_message) {
                errorHtml += `<div style="margin: 0.5rem 0; padding: 0.5rem; background: #fef2f2; border-left: 3px solid #dc2626; border-radius: 0.25rem;">`;
                if (detail.error_code) {
                  errorHtml += `<p style="margin: 0.25rem 0;"><strong>Código Shinkansen:</strong> <code style="background: #fee; padding: 2px 6px; border-radius: 3px; font-family: monospace;">${detail.error_code}</code></p>`;
                }
                if (detail.error_message) {
                  errorHtml += `<p style="margin: 0.25rem 0;"><strong>Mensaje:</strong> ${detail.error_message}</p>`;
                }
                if (detail.transaction_id) {
                  errorHtml += `<p style="margin: 0.25rem 0; font-size: 0.75rem; color: #6b7280;"><strong>Transaction ID:</strong> <span style="font-family: monospace;">${detail.transaction_id}</span></p>`;
                }
                errorHtml += `</div>`;
              } else {
                // Errores genéricos
                const field = detail.field || detail.property || 'Campo';
                const message = detail.description || detail.message || JSON.stringify(detail);
                errorHtml += `<p style="margin: 0.25rem 0;">• <strong>${field}:</strong> ${message}</p>`;
              }
            });
          } else if (typeof errorDetails === 'object') {
            Object.keys(errorDetails).forEach((key) => {
              const value = errorDetails[key];
              const displayValue = typeof value === 'object' ? JSON.stringify(value) : value;
              errorHtml += `<p style="margin: 0.25rem 0;">• <strong>${key}:</strong> ${displayValue}</p>`;
            });
          }

          errorHtml += '</div>';
        }

        // Agregar código de error general si existe y es diferente del código de Shinkansen
        const errorCode = error?.code || error?.error?.code;
        if (errorCode && errorCode !== shinkansenErrorCode?.toUpperCase()) {
          errorHtml += `<p style="margin-top: 1rem; font-size: 0.75rem; color: #6b7280;">Código de error: ${errorCode}</p>`;
        }

        // Agregar sugerencia específica para errores de RUT
        if (errorCode === 'INVALID_RUT' || mainMessage.toLowerCase().includes('rut')) {
          errorHtml += '<hr style="margin: 1rem 0; border: none; border-top: 1px solid #fed7aa;">';
          errorHtml += '<div style="text-align: left; font-size: 0.8rem; color: #92400e; background: #fffbeb; padding: 0.75rem; border-radius: 0.375rem; border-left: 3px solid #f59e0b;">';
          errorHtml += '<p style="margin: 0 0 0.5rem 0;"><strong>💡 Sugerencia:</strong></p>';
          errorHtml += '<p style="margin: 0.25rem 0;">• Verifique que el RUT tenga el formato correcto: 12345678-9</p>';
          errorHtml += '<p style="margin: 0.25rem 0;">• El dígito verificador debe ser válido</p>';
          errorHtml += '<p style="margin: 0.25rem 0;">• No debe tener más de 10 dígitos (sin contar el guión)</p>';
          errorHtml += '</div>';
        }

        // Agregar sugerencia específica para errores de schema de Shinkansen
        if (errorCode === 'INVALID_SCHEMA_VALUE' || shinkansenErrorCode === 'invalid_schema_value') {
          errorHtml += '<hr style="margin: 1rem 0; border: none; border-top: 1px solid #fed7aa;">';
          errorHtml += '<div style="text-align: left; font-size: 0.8rem; color: #92400e; background: #fffbeb; padding: 0.75rem; border-radius: 0.375rem; border-left: 3px solid #f59e0b;">';
          errorHtml += '<p style="margin: 0 0 0.5rem 0;"><strong>💡 Sugerencia:</strong></p>';
          errorHtml += '<p style="margin: 0.25rem 0;">• El valor proporcionado no existe en el catálogo de Shinkansen</p>';
          errorHtml += '<p style="margin: 0.25rem 0;">• Verifique que el banco seleccionado sea correcto</p>';
          errorHtml += '<p style="margin: 0.25rem 0;">• Asegúrese de que el tipo de cuenta coincida con la cuenta bancaria</p>';
          errorHtml += '<p style="margin: 0.25rem 0;">• Si el error persiste, contacte al administrador para verificar la configuración</p>';
          errorHtml += '</div>';
        }

        Swal.fire({
          icon: 'error',
          title: 'Error al procesar el pago',
          html: errorHtml,
          confirmButtonText: 'Aceptar',
          confirmButtonColor: '#dc2626',
          width: '650px',
        });
      }
    });
  }

  close() {
    this.dialogRef.close();
  }
}
